import { useRef, useState, type KeyboardEvent } from "react";
import { useNavigate } from "react-router";
import { PnRow } from "../../../../shared/phone/Row";
import { Sheet } from "../../../../shared/phone/Sheet";
import { Icon } from "../../../components/ui/Icon";
import { ccApi, type AskAnswer, type HomeGoal, type HomeProduct } from "../../../lib/ccApi";
import { shortGoal } from "../text";
import { errText, useHome } from "../useCc";
import { closeSheet, ProductMark } from "./parts";

/** Today's questions, from where the goals stand. */
export function suggestions(goals: HomeGoal[]): string[] {
  const out: string[] = [];
  if (goals.some((g) => g.state === "live")) out.push("How did the ads do last night?");
  if (goals.some((g) => g.state === "getting_ready")) out.push("What is left before the ads go live?");
  if (goals.some((g) => g.state === "live" || g.state === "paused")) out.push("How is the money?");
  if (goals.some((g) => g.state === "done")) out.push("What worked on the last goal?");
  return out.slice(0, 3);
}

const STATE_WORDS: Record<string, string> = {
  live: "Running",
  paused: "Paused",
  getting_ready: "Getting ready",
  plan_ready: "Plan to review",
  planning: "Planning",
  setting_up: "Setting up",
  done: "Finished",
};

const grow = (t: HTMLTextAreaElement) => {
  t.style.height = "auto";
  t.style.height = `${Math.min(120, t.scrollHeight)}px`;
};

/**
 * Ask from Home: pick a question or type one. Flashadz finds the goal it belongs to and asks it in that goal's
 * chat; when it could be about more than one goal, the owner picks the goal first.
 */
export function AskSheet({ open, onClose, products, say }: { open: boolean; onClose: () => void; products: HomeProduct[]; say: (m: string, ok?: boolean) => void }) {
  const navigate = useNavigate();
  const { refresh } = useHome();
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [pick, setPick] = useState<{ q: string; answer: AskAnswer } | null>(null);
  const box = useRef<HTMLTextAreaElement>(null);
  // Why: the goal opens once the sheet has slid away, so the two never move at once.
  const then = useRef<{ to: string; runId?: string } | null>(null);
  const goals = products.flatMap((p) => p.goals.map((g) => ({ g, p })));
  const byId = new Map(goals.map((x) => [x.g.id, x]));

  const askIn = async (goalId: string, q: string) => {
    setBusy(true);
    try {
      const { runId } = await ccApi.send(goalId, q);
      void refresh();
      then.current = { to: `/goals/${encodeURIComponent(goalId)}/chat`, runId };
      closeSheet();
    } catch (e) {
      say(errText(e), false);
    } finally {
      setBusy(false);
    }
  };
  const ask = async (q: string) => {
    if (busy) return;
    setBusy(true);
    try {
      const answer = await ccApi.ask(q);
      if (answer.goalId) await askIn(answer.goalId, q);
      else setPick({ q, answer });
    } catch (e) {
      say(errText(e), false);
    } finally {
      setBusy(false);
    }
  };
  const send = () => {
    const q = text.trim();
    if (!q) {
      box.current?.focus();
      return;
    }
    box.current?.blur();
    void ask(q);
  };
  const key = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };
  const openAfter = (to: string) => {
    then.current = { to };
    closeSheet();
  };
  const closed = () => {
    const to = then.current;
    then.current = null;
    setPick(null);
    setText("");
    onClose();
    if (to) navigate(to.to, { state: to.runId ? { runId: to.runId } : null });
  };

  if (pick) {
    const c = pick.answer.candidates.filter((x) => byId.has(x.goalId));
    const why = new Map(c.map((x) => [x.goalId, x.why]));
    const shown = c.length === 1 ? [c[0].goalId, ...goals.map((x) => x.g.id).filter((id) => id !== c[0].goalId)] : c.length ? c.map((x) => x.goalId) : goals.map((x) => x.g.id);
    return (
      <Sheet
        open={open}
        onClose={closed}
        title="Which goal is it about?"
        left={
          <button type="button" className="pn-tbtn" onClick={() => setPick(null)}>
            Back
          </button>
        }
      >
        <p className="hm-sub">“{pick.q}”</p>
        {!c.length && <p className="hm-sub">I only work on your goals: their plans, ads, emails and results. Pick a goal to ask about it, or start a new one.</p>}
        <div className="pn-grp">
          {shown.map((id) => {
            const x = byId.get(id);
            if (!x) return null;
            return (
              <PnRow
                key={id}
                onClick={() => (c.length ? void askIn(id, pick.q) : openAfter(`/goals/${encodeURIComponent(id)}`))}
                icon={<ProductMark p={x.p} size={30} />}
                className="hm-askg"
                title={shortGoal(x.g.sentence)}
                sub={why.get(id) || STATE_WORDS[x.g.state] || ""}
                wrap
                chevron
              />
            );
          })}
          {!c.length && (
            <PnRow
              onClick={() => openAfter("/goals/new")}
              icon={<Icon name="plus" />}
              title="New goal"
              chevron
            />
          )}
        </div>
        {busy && <Typing />}
      </Sheet>
    );
  }

  const sugs = suggestions(goals.map((x) => x.g));
  return (
    <Sheet
      open={open}
      onClose={closed}
      title="Ask the agent"
      foot={
        <div className="hm-composer">
          <textarea
            ref={box}
            className="pn-inp"
            rows={1}
            placeholder="Ask anything"
            aria-label="Ask the agent"
            enterKeyHint="send"
            value={text}
            onChange={(e) => {
              setText(e.target.value);
              grow(e.target);
            }}
            onKeyDown={key}
          />
          <button type="button" className="hm-send" aria-label="Send" disabled={busy} onClick={send}>
            <Icon name="send" />
          </button>
        </div>
      }
    >
      {sugs.length > 0 && (
        <div className="pn-grp">
          {sugs.map((s) => (
            <PnRow key={s} onClick={() => void ask(s)} icon={<Icon name="ask" />} title={s} wrap chevron={false} />
          ))}
        </div>
      )}
      {busy ? <Typing /> : <p className="pn-note">The agent answers in the goal's chat, so the answer stays with the goal.</p>}
    </Sheet>
  );
}

function Typing() {
  return (
    <p className="pn-note hm-typing" aria-label="Finding the goal">
      <i />
      <i />
      <i />
    </p>
  );
}
